import { normalizeAiPhotoConfigId, type AiPhotoScope, type AiPhotoShopId, type AiPhotoEnvironmentId } from '@ai-photo-style/shared';

export const LIBRARY_LIMIT = 8;
const DB_NAME = 'ai-photo-style';
const STORE = 'results';

/** Alleen het gegenereerde beeld. De geüploade foto hoort hier niet in. */
export interface LibraryRecord {
  id: string;
  styleId: string;
  /** Snapshot survives renames/deletion of the catalog look. */
  labelSnapshot?: string;
  shopId?: AiPhotoShopId;
  environmentId?: AiPhotoEnvironmentId;
  configId?: string;
  revision?: string;
  width: number;
  height: number;
  createdAt: number;
  blob: Blob;
}

export function trimToNewest<T extends { createdAt: number }>(
  records: T[],
  limit = LIBRARY_LIMIT,
): { keep: T[]; drop: T[] } {
  const sorted = [...records].sort((a, b) => b.createdAt - a.createdAt);
  return { keep: sorted.slice(0, limit), drop: sorted.slice(limit) };
}

function hasIdb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function isQuotaError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED');
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'id' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function matchesLibraryScope(row: Pick<LibraryRecord, 'shopId' | 'environmentId' | 'configId' | 'revision'>, scope: AiPhotoScope): boolean {
  try {
    if (normalizeAiPhotoConfigId(row.configId) !== normalizeAiPhotoConfigId(scope.configId)) return false;
  } catch { return false; }
  if (!row.shopId) return !row.environmentId && !row.revision && row.configId === undefined && scope.shopId === 'hippe' && scope.environmentId === 'production';
  return row.shopId === scope.shopId && row.environmentId === scope.environmentId;
}

/** Same partition for normal eviction and quota-recovery eviction. */
export function libraryScopeKey(row: Pick<LibraryRecord, 'shopId' | 'environmentId' | 'configId'>): string {
  return `${row.shopId ?? 'hippe'}--${row.environmentId ?? 'production'}--${normalizeAiPhotoConfigId(row.configId)}`;
}

export async function listIllustrations(scope?: AiPhotoScope): Promise<LibraryRecord[]> {
  if (!hasIdb()) return [];
  const db = await openDb();
  try {
    const rows = await requestToPromise(db.transaction(STORE, 'readonly').objectStore(STORE).getAll() as IDBRequest<LibraryRecord[]>);
    return trimToNewest(scope ? rows.filter((row) => matchesLibraryScope(row, scope)) : rows).keep;
  } finally {
    db.close();
  }
}

export async function getIllustration(id: string, scope?: AiPhotoScope): Promise<LibraryRecord | undefined> {
  if (!hasIdb()) return undefined;
  const db = await openDb();
  try {
    const row = await requestToPromise(db.transaction(STORE, 'readonly').objectStore(STORE).get(id) as IDBRequest<LibraryRecord | undefined>);
    return row && (!scope || matchesLibraryScope(row, scope)) ? row : undefined;
  } finally {
    db.close();
  }
}

async function writeTrimmed(record: LibraryRecord): Promise<void> {
  const db = await openDb();
  try {
    const existing = await requestToPromise(
      db.transaction(STORE, 'readonly').objectStore(STORE).getAll() as IDBRequest<LibraryRecord[]>,
    );
    const without = existing.filter((row) => row.id !== record.id);
    // A busy config must not evict another shop/environment/config's saved results.
    const key = libraryScopeKey(record);
    const { drop } = trimToNewest([...without.filter((row) => {
      try { return libraryScopeKey(row) === key; } catch { return false; }
    }), record]);
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    store.put(record);
    for (const row of drop) store.delete(row.id);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Schrijft een geslaagd resultaat. Een vol of ontbrekend quota laat de generatie verder lopen. */
export async function rememberIllustration(record: LibraryRecord): Promise<void> {
  if (!hasIdb()) return;
  try {
    await writeTrimmed(record);
  } catch (err) {
    if (!isQuotaError(err)) return;
    try {
      const rows = await listIllustrations({ shopId: record.shopId ?? 'hippe', environmentId: record.environmentId ?? 'production', configId: record.configId });
      const oldest = [...rows].sort((a, b) => a.createdAt - b.createdAt)[0];
      if (oldest && oldest.id !== record.id) {
        const db = await openDb();
        try {
          const tx = db.transaction(STORE, 'readwrite');
          tx.objectStore(STORE).delete(oldest.id);
          await new Promise<void>((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
          });
        } finally {
          db.close();
        }
      }
      await writeTrimmed(record);
    } catch {
      /* het beeld blijft beschikbaar in de open modal */
    }
  }
}
